import styled from '@emotion/styled';
import { Project, ProjectUser } from '@gameonly/core';
import React, { MouseEvent, MouseEventHandler } from 'react';

import ShareProject from './ShareProjectModal';

import { useSnackbar } from 'notistack';
import ButtonWithConfirm from '../ui/ButtonWithConfirm';

import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import DeleteIcon from '@mui/icons-material/Delete';
import MoreHorizOutlinedIcon from '@mui/icons-material/MoreHorizOutlined';
import ShareIcon from '@mui/icons-material/Share';
import IconButton from '@mui/material/IconButton';

import Menu from '@mui/material/Menu';
import MenuItem from '@mui/material/MenuItem';

import useUser, { isUserSubscribed } from '../authentication/context/useUser';

import CampaignIcon from '@mui/icons-material/Campaign';
import BaseBlock from '../ui/Block';
import CompletionBar from './CompletionBar';

import DoneAllIcon from '@mui/icons-material/DoneAll';
import DoneOutlinedIcon from '@mui/icons-material/DoneOutlined';
import SummarizeOutlinedIcon from '@mui/icons-material/SummarizeOutlined';
import {
  Avatar,
  Box,
  Chip,
  CircularProgress,
  ListItemIcon,
  ListItemText,
  Tooltip,
} from '@mui/material';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import SubscriptionTeasing from '../subscription/SubscriptionTeasing';
import { isProjectDemo } from './context/useProject';
import { blocks } from './ProjectsUtils';

interface ProjectCardProps {
  project: Project;
  onClick: MouseEventHandler<HTMLButtonElement | HTMLDivElement>;
  onDelete: (event: any) => Promise<Partial<Project>>;
  onDuplicate: (event: any) => Promise<Partial<Project>>;
}

const Block = styled(BaseBlock)`
  padding: 8px;
  display: flex;
  align-items: center;
  justify-content: space-between;
  border: 2px solid transparent;
  height: unset;
  cursor: pointer;
  transition: background-color 0.2s ease-in-out;
  position: relative;
  height: 150px;
  width: 200px;
  .hzr {
    font-size: 10px;
  }
  svg {
    transition: fill 0.2s ease-in-out;
  }
  > div:last-of-type {
    width: 100%;
    display: flex;
    align-items: center;
    gap: 8px;
    > div {
      flex: 1;
    }
  }
  &:hover {
    .h6r {
      font-weight: bold;
    }
  }
`;

const Header = styled.div`
  width: 100%;
  display: flex;
  gap: 8px;
`;

const TitleContainer = styled.div`
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  display: flex;
  align-items: center;
  justify-content: center;
  text-align: center;
`;

const ProjectCard = ({
  project,
  onClick,
  onDelete,
  onDuplicate,
}: ProjectCardProps) => {
  const { user } = useUser();
  const [openShare, setOpenShare] = React.useState<boolean>(false);
  const [anchorEl, setAnchorEl] = React.useState<null | HTMLElement>(null);
  const openMenu = Boolean(anchorEl);
  const navigate = useNavigate();
  const { enqueueSnackbar } = useSnackbar();
  const [duplicateLoading, setDuplicateLoading] =
    React.useState<boolean>(false);
  const { t } = useTranslation(['project']);

  const isShareProject = project.users.some(
    (u: ProjectUser) => u.id === user?._id && u.role === 'owner',
  );

  const handleOpenMenu = (event: React.MouseEvent<HTMLButtonElement>) => {
    event.stopPropagation();
    event.preventDefault();
    setAnchorEl(event.currentTarget);
  };

  const handleCloseMenu = (event: MouseEvent<HTMLElement>) => {
    event.stopPropagation();
    event.preventDefault();
    setAnchorEl(null);
  };

  const handleDelete = async (event: MouseEvent<HTMLButtonElement>) => {
    event.stopPropagation();
    await onDelete(event);
  };

  const handleShare = async (event: MouseEvent<HTMLElement>) => {
    event.stopPropagation();
    setOpenShare(true);
  };

  const handleDuplicate = async (event: MouseEvent<HTMLElement>) => {
    event.stopPropagation();
    setDuplicateLoading(true);
    const duplicatedProject = await onDuplicate(event);
    if (duplicatedProject) {
      setDuplicateLoading(false);
      enqueueSnackbar('Projet dupliqué', { variant: 'success' });
      navigate(`/project/${duplicatedProject._id}`);
    }
  };

  const isDemo = isProjectDemo(project);

  const isOwner = project.users.find(
    (u) => u.id === user?._id && u.role === 'owner',
  );

  return (
    <>
      <ShareProject
        open={openShare}
        onClose={() => setOpenShare(false)}
        project={project}
      />
      <Block onClick={onClick}>
        {isDemo && (
          <div style={{ position: 'absolute', top: '6px', left: '6px' }}>
            <Tooltip title="Jeu démo">
              <CampaignIcon fontSize="small" htmlColor="#ccc" />
            </Tooltip>
          </div>
        )}
        <IconButton
          sx={{ position: 'absolute', top: '6px', right: '6px', padding: 0 }}
          onClick={handleOpenMenu}
        >
          <MoreHorizOutlinedIcon />
        </IconButton>
        <Menu anchorEl={anchorEl} open={openMenu} onClose={handleCloseMenu}>
          
          {/* DELETE */}
          {isOwner && !project.validatedAt && (
            <MenuItem>
              <ListItemIcon>
                <DeleteIcon fontSize="small" />
              </ListItemIcon>
              <ListItemText>
                <ButtonWithConfirm
                  onConfirm={handleDelete}
                  modalTitle={t('project:delete-button.modalTitle')}
                  modalContent={t('project:delete-button.modalText')}
                >
                  {t('project:delete-button.label')}
                </ButtonWithConfirm>
              </ListItemText>
            </MenuItem>
          )}

          {/* SHARE */}
          {isShareProject &&
            (isUserSubscribed(user) ? (
              <MenuItem onClick={handleShare}>
                <ListItemIcon>
                  <ShareIcon fontSize="small" />
                </ListItemIcon>
                <ListItemText>
                  <p className="hxr">{t('project:share-button.label')}</p>
                </ListItemText>
              </MenuItem>
            ) : (
              <MenuItem onClick={(e) => e.stopPropagation()}>
                <SubscriptionTeasing
                  teasingButton={t(
                    'common:subscription.shareProject.teasingButton',
                  )}
                  text={t('common:subscription.shareProject.modalText')}
                  title={t('common:subscription.shareProject.modalTitle')}
                  link={t('common:subscription.shareProject.knowMoreLink')}
                />
              </MenuItem>
            ))}

          {/* DUPLICATE */}
          {isUserSubscribed(user) ? (
            <MenuItem onClick={handleDuplicate} disabled={duplicateLoading}>
              <ListItemIcon>
                <ContentCopyIcon fontSize="small" />
              </ListItemIcon>
              <ListItemText>
                <Box sx={{ display: 'flex', alignItems: 'center' }}>
                  <p className="hxr">{t('project:duplicate.button-label')}</p>
                  {duplicateLoading && (
                    <CircularProgress
                      size={20}
                      sx={{
                        ml: 1,
                        visibility: duplicateLoading ? 'visible' : 'hidden',
                      }}
                    />
                  )}
                </Box>
              </ListItemText>
            </MenuItem>
          ) : (
            <MenuItem onClick={(e) => e.stopPropagation()}>
              <SubscriptionTeasing
                teasingButton={t(
                  'common:subscription.duplicateProject.teasingButton',
                )}
                text={t('common:subscription.duplicateProject.modalText')}
                title={t('common:subscription.duplicateProject.modalTitle')}
                link={t('common:subscription.duplicateProject.knowMoreLink')}
              />
            </MenuItem>
          )}
        </Menu>
        <Header>
        {project.isCNC && (
            <Chip
              label="CNC"
              sx={{ bgcolor: 'var(--yellow)', height: "24px", color: "white" }}
            />
          )}
          {project?.isCNC && !!project?.validatedAt && (
            <Avatar sx={{ width: '24px', height: '24px' }}>
              <Tooltip title={t('cnc-section.validated-project')}>
                <DoneAllIcon fontSize="small"/>
              </Tooltip>
            </Avatar>
          )}
          {!project?.isCNC && !!project?.finalizedAt && (
            <Avatar sx={{ width: '24px', height: '24px' }}>
              <Tooltip title={t('finalized-section.validated-title')}>
                <DoneOutlinedIcon fontSize="small"/>
              </Tooltip>
            </Avatar>
          )}
        </Header>
        <TitleContainer>
          <p className="h6r">
            {project.name}
          </p>
        </TitleContainer>
        <div>
          <div>
            <CompletionBar completionRate={project.completionRate} color={blocks[project.model.type as keyof typeof blocks].barColor} />
          </div>
          <p className="hzb">{project.completionRate}%</p>
        </div>
      </Block>
    </>
  );
};

export default ProjectCard;
